/**
 * MarketingConsentFields.tsx — the two consent boxes and the words around them.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: marketing consent (2026-09-11).
 *
 * Rendered by BOTH places a patient can give or change marketing consent: the
 * questionnaire's consent screen (`journey/QuestionCard`'s "Keeping in touch"
 * block, below the required declarations — it moved there from the eligible
 * Result screen on 2026-09-23 so consent is one screen, not two extra clicks)
 * and the public `/preferences` page. One component so the two cannot show
 * different words, a different order or a different privacy link — the version
 * id stored with the choice (`consentWording.ts`) has to describe what was on
 * screen, wherever that was. Each caller supplies its own heading (an `<h2>`
 * block label on the consent screen, the page's `<h1>` on /preferences) and
 * names this group with it through `labelledBy`.
 *
 * WHAT THE LAW ASKS OF THE BOXES, and where each property lives:
 *  - SEPARATE and SPECIFIC (PECR reg. 22, UK GDPR Art 7(2)): one native
 *    checkbox per channel, each with its own programmatic label, never a single
 *    "keep me informed" box.
 *  - UNTICKED by default: this component owns no state at all. It renders the
 *    `choices` it is given, and both callers start from `false` — the consent
 *    screen ALWAYS (its one read, the WhatsApp offer check, carries the stored
 *    choice and deliberately drops it: that lane is grants-only, and a box
 *    pre-ticked from a stored value is not consent given on that screen),
 *    /preferences unless the SERVER says this person already said yes (a
 *    returning patient sees their own stored choice — "shown and editable").
 *  - WhatsApp only where it can mean something — the approved words say "on
 *    the mobile number I gave you". The caller decides `showWhatsapp`:
 *    /preferences on a UK mobile on file OR a grant already recorded (the
 *    second so a person can always withdraw what they gave); the consent screen
 *    on a UK mobile ALONE, as its draft read reports it (grants-only, so there
 *    is nothing there to withdraw), and never while that read is unanswered.
 *
 * A11y: a `role="group"` named by the caller's heading and described by the
 * intro and the withdraw line, so a screen reader announces the context before
 * the first box. The intro and the withdraw line sit OUTSIDE the labels on
 * purpose — a label is the name of one box, not a paragraph about both.
 * `CheckboxRow` makes the whole row the click target (the kit's 44px grammar).
 */
import { useId, type ReactNode } from 'react';
import type { MarketingChannel } from '../api/types';
import { PRIVACY_URL } from '../lib/bookingSite';
import { CheckboxRow } from '../ui/kit';
import { MARKETING_CONSENT_WORDING as WORDING } from './consentWording';

/** One boolean per channel — the full desired state, as the server takes it. */
export interface MarketingChoices {
  email: boolean;
  whatsapp: boolean;
}

/** Both boxes unticked: where every person starts until the server says otherwise. */
export const UNTICKED: MarketingChoices = { email: false, whatsapp: false };

export function MarketingConsentFields({
  labelledBy,
  choices,
  showWhatsapp,
  disabled = false,
  busy = false,
  onToggle,
  className,
  beforeBoxes,
}: {
  /** The id of the caller's visible heading — it names this group. */
  labelledBy: string;
  choices: MarketingChoices;
  showWhatsapp: boolean;
  disabled?: boolean;
  /** True while the stored choice is still being read — the boxes wait for it. */
  busy?: boolean;
  onToggle: (channel: MarketingChannel) => void;
  /**
   * LAYOUT ONLY — a class on the group so a caller can arrange the same
   * elements (the consent step lays the two boxes side by side on desktop).
   * It can move the words; it can never change, reorder or drop them. Absent
   * (/preferences) → no attribute, the markup exactly as before.
   */
  className?: string;
  /**
   * A control that acts on the boxes, placed straight BEFORE them and after the
   * intro — the q15 block's "Yes to both (email and WhatsApp)" select-all
   * (CTO, 2026-09-27, `journey/QuestionCard` `KeepingInTouchSelectAll`; it
   * replaced the 2026-09-26 "Tick both" button that sat after the boxes). It is
   * the caller's, and it works only through `onToggle`, one channel at a time:
   * it adds no state of its own and no words to the consent itself — its label
   * is a CONTROL label, not consent wording, so it is not part of
   * `mc-2026-09-11-d1` and does not move the version id. Absent (/preferences)
   * → the markup exactly as before.
   */
  beforeBoxes?: ReactNode;
}) {
  const introId = useId();
  const withdrawId = useId();

  return (
    <div
      role="group"
      className={className}
      aria-labelledby={labelledBy}
      aria-describedby={`${introId} ${withdrawId}`}
      aria-busy={busy || undefined}
    >
      <p id={introId} style={{ margin: '0 0 4px', fontSize: '14.5px', lineHeight: 1.5, color: 'var(--gps-text)' }}>
        {WORDING.intro}
      </p>
      {beforeBoxes}
      <CheckboxRow label={WORDING.email} checked={choices.email} disabled={disabled} onChange={() => onToggle('email')} />
      {showWhatsapp ? (
        <CheckboxRow
          label={WORDING.whatsapp}
          checked={choices.whatsapp}
          disabled={disabled}
          onChange={() => onToggle('whatsapp')}
        />
      ) : null}
      <p id={withdrawId} style={{ margin: '6px 0 0', fontSize: '13px', lineHeight: 1.5, color: 'var(--gps-muted)' }}>
        {WORDING.withdraw}
      </p>
      {/* The footer's privacy notice (lib/bookingSite) — the same address, not a
          new one. New tab, so the choices on this screen survive the detour; the
          sr-only suffix says so, because a silent new window is disorienting. */}
      <p style={{ margin: '6px 0 0', fontSize: '13px', lineHeight: 1.5, color: 'var(--gps-muted)' }}>
        How we use your details is explained in our{' '}
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gps-link)' }}>
          privacy notice<span className="gps-sr-only"> (opens in a new tab)</span>
        </a>
        .
      </p>
    </div>
  );
}
